import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import useSWR from "swr";
import {
  type WorkflowTemplate,
  type WorkflowTemplateListItem,
  type ApplyDueDateRow,
} from "~/api/types";
import { simpleFetcher } from "~/api/fetcher";
import { useWorkflowTemplates } from "~/api/v2/useWorkflowTemplates";
import { useDocumentVersions } from "~/api/v2/useDocumentVersions";
import { DocumentVersionStatus } from "~/config/types";
import { useTags } from "~/api/swr/useTags";
import {
  createWorkflowTemplate,
  updateWorkflowTemplate,
  deleteWorkflowTemplates,
  applyWorkflowTemplate,
} from "~/api/v2/requests";
import { useTemplateEditor } from "~/hooks/useTemplateEditor";
import { Button } from "~/atoms/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/atoms/tabs";
import HeadingPage from "~/molecules/HeadingPage";
import {
  WorkflowTemplateSidebar,
  WorkflowTemplateSidebarSkeleton,
} from "~/molecules/WorkflowTemplateSidebar";
import {
  WorkflowTemplateEditor,
  WorkflowTemplateEditorSkeleton,
} from "~/organisms/WorkflowTemplateEditor";
import {
  WorkflowTemplateDocumentsTable,
  type EligibleDocument,
} from "~/organisms/WorkflowTemplateDocumentsTable";
import {
  ApplyTemplateDatesDialog,
  type DueDateRow,
  type ApplyDocument,
  type ApplyStep,
} from "~/organisms/ApplyTemplateDatesDialog";
import { toast } from "react-toastify";
import { UnsavedChangesAlert } from "~/molecules/UnsavedChangesAlert";
import { ConfirmDialog } from "~/molecules/ConfirmDialog";
import { Skeleton } from "~/atoms/skeleton";
import { Info, LayoutTemplate } from "lucide-react";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
} from "~/atoms/empty";
import { toISODateTimeString } from "~/helpers/date";

/** Sentinel ID used for a new, unsaved template */
const NEW_TEMPLATE_ID = -1;

export default function WorkflowTemplatesPage() {
  const { t } = useTranslation();

  const {
    data: templates,
    isLoading: templatesLoading,
    mutate: mutateTemplates,
  } = useWorkflowTemplates();

  const editor = useTemplateEditor();

  const {
    data: versions,
    isLoading: versionsLoading,
    mutate: mutateVersions,
  } = useDocumentVersions({
    statuses: [DocumentVersionStatus.draft],
  });
  const {
    data: documentVersions,
    isLoading: documentVersionsLoading,
    mutate: mutateDocumentVersions,
  } = useDocumentVersions({
    statuses: [DocumentVersionStatus.draft],
    includeWorkflowData: true,
  });
  const { data: tags } = useTags();

  const tagMap = useMemo(() => {
    const map = new Map<number, string>();
    if (tags) {
      for (const tag of tags) {
        map.set(tag.id, tag.title);
      }
    }
    return map;
  }, [tags]);

  const ongoingVersionIds = useMemo(() => {
    const ids = new Set<number>();
    if (!documentVersions) return ids;
    for (const dv of documentVersions) {
      if (dv.isOngoing) {
        ids.add(dv.id);
      }
    }
    return ids;
  }, [documentVersions]);

  const workflowTemplatesByVersionId = useMemo(() => {
    const nameMap = new Map<number, string | null>();
    const idMap = new Map<number, number | null>();
    if (documentVersions) {
      for (const dv of documentVersions) {
        nameMap.set(dv.id, dv.workflowTemplateName ?? null);
        idMap.set(dv.id, dv.workflowTemplateId ?? null);
      }
    }
    return { nameMap, idMap };
  }, [documentVersions]);

  const eligibleDocuments: EligibleDocument[] = useMemo(() => {
    if (!versions) return [];
    return versions
      .filter((dv) => !ongoingVersionIds.has(dv.id))
      .map((dv) => ({
        id: dv.id,
        title: dv.title,
        category: dv.document.category,
        tagIds: dv.document.tagIds,
        currentTemplateId:
          workflowTemplatesByVersionId.idMap.get(dv.id) ?? null,
        currentTemplateName:
          workflowTemplatesByVersionId.nameMap.get(dv.id) ?? null,
        owner: dv.document.owner
          ? {
              userId: dv.document.owner.userId,
              firstName: dv.document.owner.firstName,
              lastName: dv.document.owner.lastName,
            }
          : null,
        tier: dv.document.tier?.title ?? null,
        reviewFrom: dv.reviewFrom ?? null,
        reviewTo: dv.reviewTo ?? null,
      }));
  }, [versions, ongoingVersionIds, workflowTemplatesByVersionId]);

  const documentsLoading = versionsLoading || documentVersionsLoading;

  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(
    null,
  );
  const [isNewTemplate, setIsNewTemplate] = useState(false);

  const { data: selectedTemplateDetail, mutate: mutateDetail } =
    useSWR<WorkflowTemplate>(
      selectedTemplateId !== null && selectedTemplateId !== NEW_TEMPLATE_ID
        ? `/api/v2/workflow-templates/${selectedTemplateId}`
        : null,
      simpleFetcher,
    );

  const [isApplying, setIsApplying] = useState(false);

  const [selectedDocumentIds, setSelectedDocumentIds] = useState<Set<number>>(
    new Set(),
  );

  const [deleteTargetIds, setDeleteTargetIds] = useState<number[]>([]);
  const isBulkDelete = deleteTargetIds.length > 1;
  const singleDeleteTarget =
    deleteTargetIds.length === 1
      ? deleteTargetIds[0] === NEW_TEMPLATE_ID
        ? {
            name:
              editor.editedName || t(($) => $.workflowTemplates.newTemplate),
          }
        : templates?.find((tmpl) => tmpl.id === deleteTargetIds[0])
      : null;

  const [showApplyConfirm, setShowApplyConfirm] = useState(false);
  const [showDatesDialog, setShowDatesDialog] = useState(false);

  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
  const unsavedAlertOpen = pendingAction !== null;

  const sidebarTemplates: WorkflowTemplateListItem[] = useMemo(() => {
    const list = templates ?? [];
    if (isNewTemplate) {
      return [
        ...list,
        {
          id: NEW_TEMPLATE_ID,
          name: editor.editedName || t(($) => $.workflowTemplates.newTemplate),
          description: editor.editedDescription,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          stepCount: editor.editedSteps.length,
          isDefault: false,
        },
      ];
    }
    return list;
  }, [
    templates,
    isNewTemplate,
    editor.editedName,
    editor.editedDescription,
    editor.editedSteps.length,
    t,
  ]);

  const editorTemplate: WorkflowTemplate | null = useMemo(() => {
    if (selectedTemplateId === null) return null;

    if (isNewTemplate) {
      return {
        id: NEW_TEMPLATE_ID,
        name: editor.editedName,
        description: editor.editedDescription,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        steps: [],
        isDefault: false,
      };
    }

    return selectedTemplateDetail ?? null;
  }, [
    selectedTemplateId,
    isNewTemplate,
    editor.editedName,
    editor.editedDescription,
    selectedTemplateDetail,
  ]);

  const affectedDocumentCount = useMemo(() => {
    let count = 0;
    for (const doc of eligibleDocuments) {
      if (selectedDocumentIds.has(doc.id) && doc.currentTemplateId !== null) {
        count++;
      }
    }
    return count;
  }, [eligibleDocuments, selectedDocumentIds]);

  const performSelectTemplate = useCallback(
    (id: number) => {
      if (id === NEW_TEMPLATE_ID) return;

      if (id === selectedTemplateId && !isNewTemplate) {
        setSelectedTemplateId(null);
        setSelectedDocumentIds(new Set());
        editor.setLastLoadedTemplateId(null);
        editor.setHasUnsavedChanges(false);
        return;
      }

      setIsNewTemplate(false);

      setSelectedTemplateId(id);
      setSelectedDocumentIds(new Set());
      editor.setHasUnsavedChanges(false);
    },
    [selectedTemplateId, isNewTemplate, editor],
  );

  const selectTemplate = useCallback(
    (id: number) => {
      if (id === NEW_TEMPLATE_ID) return;

      if (editor.hasUnsavedChanges || isNewTemplate) {
        setPendingAction(() => () => performSelectTemplate(id));
        return;
      }

      performSelectTemplate(id);
    },
    [
      editor.hasUnsavedChanges,
      performSelectTemplate,
      isNewTemplate,
      setPendingAction,
    ],
  );

  if (
    selectedTemplateId === selectedTemplateDetail?.id &&
    editor.lastLoadedTemplateId !== selectedTemplateDetail.id &&
    !isNewTemplate
  ) {
    editor.loadFromTemplate(selectedTemplateDetail);
  }

  const performCreate = useCallback(() => {
    setIsNewTemplate(true);
    setSelectedTemplateId(NEW_TEMPLATE_ID);
    editor.resetForNewTemplate();
    setSelectedDocumentIds(new Set());
  }, [editor]);

  const handleCreate = useCallback(() => {
    if (editor.hasUnsavedChanges || isNewTemplate) {
      setPendingAction(() => () => performCreate());
      return;
    }
    performCreate();
  }, [
    editor.hasUnsavedChanges,
    performCreate,
    isNewTemplate,
    setPendingAction,
  ]);

  const handleDelete = (id: number) => {
    const isDefault =
      templates?.find((tmpl) => tmpl.id === id)?.isDefault ?? false;
    if (isDefault) {
      toast.error(t(($) => $.workflowTemplates.cannotDeleteDefault));
      return;
    }
    setDeleteTargetIds([id]);
  };

  const handleBulkDelete = (ids: number[]) => {
    const defaultIds = new Set(
      (templates ?? []).filter((tmpl) => tmpl.isDefault).map((tmpl) => tmpl.id),
    );
    const filtered = ids.filter((id) => !defaultIds.has(id));
    if (filtered.length === 0) return;
    setDeleteTargetIds(filtered);
  };

  const confirmDelete = async () => {
    if (deleteTargetIds.length === 0) return;

    const hasNewTemplate = deleteTargetIds.includes(NEW_TEMPLATE_ID);
    const apiIds = deleteTargetIds.filter((id) => id !== NEW_TEMPLATE_ID);

    if (hasNewTemplate) {
      setIsNewTemplate(false);
    }

    if (apiIds.length > 0) {
      try {
        await deleteWorkflowTemplates(apiIds);
        await mutateTemplates();
        toast.success(t(($) => $.workflowTemplates.deleteSuccess));
      } catch {
        toast.error(t(($) => $.workflowTemplates.deleteError));
        setDeleteTargetIds([]);
        return;
      }
    }

    if (
      selectedTemplateId !== null &&
      deleteTargetIds.includes(selectedTemplateId)
    ) {
      setSelectedTemplateId(null);
      setSelectedDocumentIds(new Set());
      editor.setLastLoadedTemplateId(null);
    }

    setDeleteTargetIds([]);
  };

  const handleSaveTemplate = async () => {
    if (selectedTemplateId === null) return;

    const trimmedName = editor.editedName.trim();
    const isDefault = selectedTemplateDetail?.isDefault ?? false;
    if (!trimmedName && !isDefault) {
      toast.error(t(($) => $.workflowTemplates.templateNameRequired));
      return;
    }

    const invalidStep = editor.editedSteps.find((step) => !step.name.trim());
    if (invalidStep) {
      toast.error(t(($) => $.commons.workflow.allStepsMustHaveName));
      return;
    }

    editor.setIsSaving(true);

    try {
      const payload = {
        name: trimmedName || null,
        description: editor.editedDescription,
        steps: editor.editedSteps.map((step) => ({
          id: step.id,
          name: step.name,
          description: step.description || null,
          type: step.type,
          dueAt: step.dueAt,
          users: step.users.map((u) => ({
            userId: u.userId,
            permission: u.permission,
          })),
        })),
      };

      let savedTemplate: WorkflowTemplate;

      if (isNewTemplate) {
        savedTemplate = await createWorkflowTemplate(payload);
        setIsNewTemplate(false);
        setSelectedTemplateId(savedTemplate.id);
      } else {
        savedTemplate = await updateWorkflowTemplate(
          selectedTemplateId,
          payload,
        );
      }

      editor.loadFromTemplate(savedTemplate);

      await mutateTemplates();
      await mutateDocumentVersions();
      await mutateDetail(savedTemplate, { revalidate: false });

      toast.success(t(($) => $.workflowTemplates.saveSuccess));
    } catch {
      toast.error(t(($) => $.workflowTemplates.saveError));
    } finally {
      editor.setIsSaving(false);
    }
  };

  const handleApply = () => {
    if (
      selectedTemplateId === null ||
      isNewTemplate ||
      selectedDocumentIds.size === 0
    )
      return;

    if (affectedDocumentCount > 0) {
      setShowApplyConfirm(true);
      return;
    }

    setShowDatesDialog(true);
  };

  const handleOverrideConfirm = () => {
    setShowApplyConfirm(false);
    setShowDatesDialog(true);
  };

  const handleConfirmApply = async (
    dueDateRows: Record<number, DueDateRow>,
  ) => {
    if (selectedTemplateId === null || isNewTemplate) return;

    setShowDatesDialog(false);
    setIsApplying(true);

    const dueDates: Record<number, ApplyDueDateRow> = {};
    for (const [docId, row] of Object.entries(dueDateRows)) {
      dueDates[Number(docId)] = {
        reviewFrom: toISODateTimeString(row.reviewFrom),
        reviewTo: toISODateTimeString(row.reviewTo),
        stepDueDates: row.stepDueDates.map((d) => toISODateTimeString(d)),
      };
    }

    try {
      await applyWorkflowTemplate(
        selectedTemplateId,
        [...selectedDocumentIds],
        dueDates,
      );
      await Promise.all([mutateDocumentVersions(), mutateVersions()]);
      setSelectedDocumentIds(new Set());
      toast.success(t(($) => $.workflowTemplates.applySuccess));
    } catch {
      toast.error(t(($) => $.workflowTemplates.applyError));
    } finally {
      setIsApplying(false);
    }
  };

  const datesDialogDocuments: ApplyDocument[] = useMemo(() => {
    return eligibleDocuments
      .filter((d) => selectedDocumentIds.has(d.id))
      .map((d) => ({
        id: d.id,
        title: d.title,
        reviewFrom: d.reviewFrom ? new Date(d.reviewFrom) : null,
        reviewTo: d.reviewTo ? new Date(d.reviewTo) : null,
      }));
  }, [eligibleDocuments, selectedDocumentIds]);

  const datesDialogSteps: ApplyStep[] = useMemo(() => {
    if (!editorTemplate || isNewTemplate) return [];
    return editor.editedSteps.map((step, i) => ({
      name: step.name,
      order: i,
    }));
  }, [editorTemplate, isNewTemplate, editor.editedSteps]);

  const isDetailsLoading =
    selectedTemplateId !== null && !isNewTemplate && !selectedTemplateDetail;

  const editorActions = (
    <>
      {!(selectedTemplateDetail?.isDefault ?? false) && (
        <Button
          variant="destructive"
          onClick={() => handleDelete(selectedTemplateId ?? NEW_TEMPLATE_ID)}
        >
          {t(($) => $.workflowTemplates.deleteTemplate)}
        </Button>
      )}
      <Button
        onClick={handleSaveTemplate}
        disabled={
          editor.isSaving || (!isNewTemplate && !editor.hasUnsavedChanges)
        }
      >
        {editor.isSaving
          ? t(($) => $.commons.buttons.saving)
          : t(($) => $.workflowTemplates.saveTemplate)}
      </Button>
    </>
  );

  const documentsTableActions = (
    <>
      {isNewTemplate && (
        <span className="text-muted-foreground flex items-center gap-1.5 text-sm">
          <Info className="size-4 shrink-0" />
          {t(($) => $.workflowTemplates.documentsTable.saveTemplateFirst)}
        </span>
      )}
      <Button
        onClick={handleApply}
        disabled={isNewTemplate || selectedDocumentIds.size === 0 || isApplying}
      >
        {isApplying
          ? t(($) => $.workflowTemplates.applying)
          : t(($) => $.workflowTemplates.applyToDocuments, {
              count: selectedDocumentIds.size,
            })}
      </Button>
    </>
  );

  return (
    <div className="container flex grow flex-col gap-6 py-7">
      <HeadingPage title={t(($) => $.workflowTemplates.title)} />

      <div className="flex min-h-150 gap-6">
        <div className="sticky top-6 w-75 shrink-0 self-start">
          {templatesLoading ? (
            <WorkflowTemplateSidebarSkeleton />
          ) : (
            <WorkflowTemplateSidebar
              templates={sidebarTemplates}
              selectedTemplateId={selectedTemplateId}
              onSelect={selectTemplate}
              onCreate={handleCreate}
              onDelete={handleDelete}
              onBulkDelete={handleBulkDelete}
            />
          )}
        </div>

        <div className="min-w-0 flex-1">
          {editorTemplate && !isDetailsLoading ? (
            <Tabs defaultValue="editor">
              <TabsList layout="compact">
                <TabsTrigger value="editor">
                  {t(($) => $.workflowTemplates.tabs.editor)}
                </TabsTrigger>
                <TabsTrigger value="apply">
                  {t(($) => $.workflowTemplates.tabs.applyToDocuments)}
                </TabsTrigger>
              </TabsList>

              <TabsContent
                value="editor"
                forceMount
                className="data-[state=inactive]:hidden"
              >
                <WorkflowTemplateEditor
                  template={editorTemplate}
                  editedSteps={editor.editedSteps}
                  setEditedSteps={editor.setEditedSteps}
                  editedName={editor.editedName}
                  onNameChange={(name) => {
                    editor.setEditedName(name);
                    editor.setHasUnsavedChanges(true);
                  }}
                  editedDescription={editor.editedDescription}
                  onDescriptionChange={(desc) => {
                    editor.setEditedDescription(desc);
                    editor.setHasUnsavedChanges(true);
                  }}
                  setHasUnsavedChanges={editor.setHasUnsavedChanges}
                  actions={editorActions}
                />
              </TabsContent>

              <TabsContent
                value="apply"
                forceMount
                className="data-[state=inactive]:hidden"
              >
                <WorkflowTemplateDocumentsTable
                  templateId={isNewTemplate ? null : selectedTemplateId}
                  documents={eligibleDocuments}
                  tagMap={tagMap}
                  isLoading={documentsLoading}
                  selectedIds={selectedDocumentIds}
                  onSelectionChange={setSelectedDocumentIds}
                  actions={documentsTableActions}
                />
              </TabsContent>
            </Tabs>
          ) : (
            <div className="animate-in fade-in duration-200">
              {isDetailsLoading || templatesLoading ? (
                <div className="w-full">
                  <div className="flex gap-2">
                    <Skeleton className="h-9 w-24 rounded-md" />
                    <Skeleton className="h-9 w-36 rounded-md" />
                  </div>
                  <WorkflowTemplateEditorSkeleton />
                </div>
              ) : (
                <div className="flex h-full items-center justify-center">
                  <Empty className="h-fit border-0">
                    <EmptyHeader>
                      <EmptyMedia variant="icon">
                        <LayoutTemplate />
                      </EmptyMedia>
                      <EmptyDescription>
                        {(templates?.length ?? 0) === 0 && !isNewTemplate
                          ? t(($) => $.workflowTemplates.noTemplates)
                          : t(($) => $.workflowTemplates.noTemplateSelected)}
                      </EmptyDescription>
                    </EmptyHeader>
                  </Empty>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={deleteTargetIds.length > 0}
        onOpenChange={(open) => {
          if (!open) setDeleteTargetIds([]);
        }}
        title={
          isBulkDelete
            ? t(($) => $.workflowTemplates.deleteConfirm.titleBulk, {
                count: deleteTargetIds.length,
              })
            : t(($) => $.workflowTemplates.deleteConfirm.title)
        }
        description={
          isBulkDelete
            ? t(($) => $.workflowTemplates.deleteConfirm.descriptionBulk, {
                count: deleteTargetIds.length,
              })
            : t(($) => $.workflowTemplates.deleteConfirm.description, {
                name: singleDeleteTarget?.name ?? "",
              })
        }
        confirmLabel={t(($) => $.commons.buttons.delete)}
        onConfirm={confirmDelete}
      />

      <ConfirmDialog
        open={showApplyConfirm}
        onOpenChange={(open) => {
          if (!open) setShowApplyConfirm(false);
        }}
        title={t(($) => $.workflowTemplates.applyConfirm.title)}
        description={t(($) => $.workflowTemplates.applyConfirm.description, {
          count: affectedDocumentCount,
        })}
        confirmLabel={t(($) => $.workflowTemplates.applyConfirm.confirm)}
        onConfirm={handleOverrideConfirm}
      />

      <ApplyTemplateDatesDialog
        open={showDatesDialog}
        onOpenChange={setShowDatesDialog}
        templateName={
          editor.editedName || t(($) => $.workflowTemplates.defaultBadge)
        }
        steps={datesDialogSteps}
        documents={datesDialogDocuments}
        onApply={handleConfirmApply}
        isApplying={isApplying}
      />

      <UnsavedChangesAlert
        hasUnsavedChanges={editor.hasUnsavedChanges || isNewTemplate}
        manualOpen={unsavedAlertOpen}
        onManualOpenChange={(open) => {
          if (!open) setPendingAction(null);
        }}
        onManualLeave={() => {
          pendingAction?.();
          setPendingAction(null);
        }}
      />
    </div>
  );
}
